import { Fragment } from "react";
import { PaxErrorPage, PaxTab, PaxTabPanel, useTranslation } from "@ncino/pax-ui";
import { useGetPoolForSellPageQuery } from "@/services/graphql/reactQuery";
import { z } from "zod";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import { Box, Stack, Typography, Tabs, Divider } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { makeDateFormatter } from "../manage/-internal/ValidationStep/types";
import { LoadingPageComponent } from "@/shared/components/LoadingPage";
import { TearSheet } from "@/shared/components/PoolDetails/TearSheet";
import { useEffectivePortfolioId } from "@/shared/hooks/useActivePortfolio";
import { PoolAnalytics } from "@/shared/components/PoolDetails/PoolAnalytics";
import { PoolLoanList } from "@/shared/components/PoolDetails/PoolLoanList";
import { useSellRouteContext } from "./-internal/SellRouteContext";
import { StatusBadge } from "./-internal/sellTableUtil";

const tabSchema = z.enum(["tearsheet", "analytics", "loan-list"]);
type TabValue = z.infer<typeof tabSchema>;

export const Route = createFileRoute("/sell/$poolId")({
  component: RouteComponent,
  validateSearch: z.object({
    tab: tabSchema.catch("tearsheet"),
  }),
});

function RouteComponent() {
  const { t, i18n } = useTranslation("sell");
  const locale = i18n.language.replace("_", "-");

  const { poolId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { setActive } = useSellRouteContext();

  const handleNavigateToSell = () => {
    setActive(undefined);
    navigate({ to: "/sell" });
  };

  const handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
    navigate({
      from: Route.fullPath,
      search: { tab: value },
    });
  };

  const { portfolioId, institutionId, activeDataDate: dataDate } = useEffectivePortfolioId();
  const { data: poolData, isPending } = useGetPoolForSellPageQuery({ id: Number(poolId) });
  const { id, createdDate, poolName, loanCount, listing, loans } = poolData?.pool || {};
  const { participants, servicingFeePercentage, poolType, grossYield } = poolData?.pool || {};
  const {
    carveOutPermitted,
    dueDiligencePeriod,
    dueDiligencePeriodDays,
    price,
    recourseType,
    servicingType,
    settlementType,
    listingStage,
  } = listing || {};

  const poolBalance = loans?.reduce((sum, loan) => sum + (loan.currentBalance ?? 0), 0); // TODO: update this when stored in db
  const sellerParticipant = participants?.find((p) => p.isSeller);
  const sellerRetentionDecimal = sellerParticipant?.percentage ?? 0;
  const availableBalance = poolBalance ? poolBalance * (1 - sellerRetentionDecimal) : 0;

  const formattedDate =
    isPending || !createdDate
      ? ""
      : makeDateFormatter(locale).format(new Date(createdDate).getTime());

  const loanIdNumbers = loans?.map((l) => l?.loanNumber || "");

  return (
    <PageContentContainer>
      {isPending ? (
        <LoadingPageComponent />
      ) : !id ? (
        <PaxErrorPage
          header={t("sellPage.poolTabDetails.poolNotFound")}
          subtext=""
          actionLabel={t("sellPage.poolTabDetails.returnToList")}
          onAction={handleNavigateToSell}
        />
      ) : (
        <Fragment>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">{poolName}</Typography>
              <Typography variant="body1">
                {t("sellPage.poolTabDetails.created")} {formattedDate}
              </Typography>
            </Box>
            <StatusBadge status={listingStage || ""} sx={{ marginLeft: "24px" }} />
          </Stack>

          <Tabs
            value={tab}
            onChange={handleTabChange}
            aria-label="test"
            sx={{ "& .MuiTab-root": { textTransform: "none" } }}
          >
            <PaxTab label={t("sellPage.poolTabDetails.tearSheet")} value="tearsheet" />
            <PaxTab label={t("sellPage.poolTabDetails.analytics")} value="analytics" />
            <PaxTab
              label={t("sellPage.poolTabDetails.loanList", { count: loanCount })}
              value="loan-list"
            />
          </Tabs>
          <Divider sx={{ mb: 4 }} />
          <PaxTabPanel value={tab} index="tearsheet">
            <TearSheet
              poolBalance={poolBalance || 0}
              totalLoans={loanCount || 0}
              amountAvailable={availableBalance}
              grossYield={grossYield || 0}
              assetClass={poolType || null}
              settlementType={settlementType || null}
              recourseType={recourseType || null}
              price={price || 0}
              diligencePeriodType={dueDiligencePeriod || null}
              diligenceDuration={dueDiligencePeriodDays || 0}
              carveOut={carveOutPermitted || false}
              servicingType={servicingType || null}
              servicingFee={servicingFeePercentage || 0}
            />
          </PaxTabPanel>
          <PaxTabPanel value={tab} index="analytics">
            {id && portfolioId && institutionId && dataDate && loans && (
              <PoolAnalytics
                dataDate={dataDate}
                poolId={id}
                portfolioId={portfolioId}
                institutionId={institutionId}
                loans={loanIdNumbers}
              />
            )}
          </PaxTabPanel>
          <PaxTabPanel value={tab} index="loan-list">
            <Typography>
              {loanCount && institutionId && (
                <PoolLoanList
                  poolId={id}
                  totalLoans={loanCount}
                  institutionId={institutionId}
                  isSellPage={true}
                />
              )}
            </Typography>
          </PaxTabPanel>
        </Fragment>
      )}
    </PageContentContainer>
  );
}
